import { useLanguage } from '@/contexts/LanguageContext';

export default function HomeProblem() {
  const { lang } = useLanguage();

  const sectionLabel = lang === 'en' ? 'Industry Pain Points' : lang === 'ja' ? '業界の課題' : '產業痛點';
  const heading = lang === 'en'
    ? <><span>Plastic Waste Has Value —</span><br className="hidden sm:block" /><span>It Just Needs the Right Technology</span></>
    : lang === 'ja'
    ? <><span>廃プラスチックには価値がある —</span><br className="hidden sm:block" /><span>必要なのは適切な技術だけ</span></>
    : <><span>塑膠廢棄物不是沒有價值，</span><br className="hidden sm:block" /><span>而是缺少重新升級的技術</span></>;

  const body = lang === 'en'
    ? 'Global plastic consumption continues to rise. When waste plastics can only be incinerated, landfilled, or downcycled, it results in resource waste and carbon pressure. Brand owners and manufacturers want to adopt recycled materials, but consistently face challenges with degraded properties, quality instability, processing difficulties, and incompatible mixed plastics.'
    : lang === 'ja'
    ? 'プラスチックの世界的な使用量は増加し続けています。廃プラスチックが焼却・埋立・ダウンサイクルしか選択肢がない場合、資源の無駄と炭素排出圧力が生じます。ブランドオーナーやメーカーは再生材料の採用を望んでいますが、物性低下・品質不安定・加工困難・混合プラスチックの相容性問題に直面しています。'
    : '全球塑膠使用量持續增加，廢棄塑膠若只能焚化、掩埋或降階使用，將造成資源浪費與碳排壓力。品牌端與製造端雖希望導入再生料，卻常面臨物性不足、品質不穩、加工不易、混合塑料難以處理等問題。';

  const problems = lang === 'en'
    ? [
        { icon: 'ri-error-warning-line', color: 'text-[#E67E22]', bg: 'bg-[#FEF3E2]', title: 'Degraded Mechanical Properties', desc: 'Recycled plastics subjected to repeated thermal processing suffer from chain scission, leading to reduced strength, poor durability, and inconsistent quality that fails to meet manufacturing specifications.' },
        { icon: 'ri-git-merge-line', color: 'text-[#C0392B]', bg: 'bg-[#FDECEA]', title: 'Incompatible Mixed Plastics', desc: 'Blending waste plastics from different sources and material types causes compatibility issues, resulting in unstable processing, low yields, and unpredictable quality.' },
        { icon: 'ri-plant-line', color: 'text-brand-blue', bg: 'bg-brand-blue-light', title: 'Low-Carbon Products Hard to Scale', desc: 'Brands seeking to adopt recycled materials for ESG initiatives lack stable, scalable material solutions, making it difficult to achieve low-carbon product targets in practice.' },
      ]
    : lang === 'ja'
    ? [
        { icon: 'ri-error-warning-line', color: 'text-[#E67E22]', bg: 'bg-[#FEF3E2]', title: '機械的特性の低下', desc: '繰り返しの熱加工により分子鎖が切断され、強度・耐久性が低下します。再生材料の物性は原生材料に遠く及ばず、製造仕様を満たせないことが多いです。' },
        { icon: 'ri-git-merge-line', color: 'text-[#C0392B]', bg: 'bg-[#FDECEA]', title: '混合プラスチックの相容性問題', desc: '異なる由来・材質の廃プラスチックを混合すると相容性不足が生じ、加工不安定・歩留まり低下・品質のばらつきを引き起こします。' },
        { icon: 'ri-plant-line', color: 'text-brand-blue', bg: 'bg-brand-blue-light', title: '低炭素製品の量産化が困難', desc: 'ESG目標のために再生材料を採用したいブランドは、安定した量産可能な材料ソリューションが不足しており、低炭素目標の実現が難しい状況です。' },
      ]
    : [
        { icon: 'ri-error-warning-line', color: 'text-[#E67E22]', bg: 'bg-[#FEF3E2]', title: '再生料物性不足', desc: '回收塑膠經多次熱加工後，容易出現強度下降、耐久不足與品質不穩，難以符合製造端的規格要求。' },
        { icon: 'ri-git-merge-line', color: 'text-[#C0392B]', bg: 'bg-[#FDECEA]', title: '混合塑料難相容', desc: '不同來源、不同材質的廢棄塑膠混合後，常造成加工與成型穩定性不足，良率低落且難以控制。' },
        { icon: 'ri-plant-line', color: 'text-brand-blue', bg: 'bg-brand-blue-light', title: '品牌低碳產品難落地', desc: '企業想導入再生料與 ESG 專案，但缺少能穩定量產的材料解決方案，導致低碳目標難以實現。' },
      ];

  return (
    <section className="section-padding bg-[#F5F8FB]">
      <div className="max-w-screen-xl mx-auto px-4 md:px-6">
        <div className="max-w-3xl mx-auto text-center mb-12">
          <p className="text-[1rem] font-semibold text-brand-green uppercase tracking-widest mb-3">{sectionLabel}</p>
          <h2 className="font-bold text-neutral-dark mb-5" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)', lineHeight: '1.4' }}>{heading}</h2>
          <p className="text-neutral-mid" style={{ fontSize: '1.1875rem', lineHeight: '1.75' }}>{body}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
          {problems.map((item) => (
            <div key={item.title} className="bg-white rounded-card p-7 border border-neutral-border" style={{ boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
              <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${item.bg} mb-5`}>
                <i className={`${item.icon} ${item.color} text-2xl`}></i>
              </div>
              <h3 className="font-semibold text-neutral-dark mb-3" style={{ fontSize: '1.25rem', lineHeight: '1.5' }}>{item.title}</h3>
              <p className="text-neutral-mid" style={{ fontSize: '1.0625rem', lineHeight: '1.75' }}>{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
